import { StateCreator } from 'zustand';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';

export const PersistKeys = {
    SHOP_IDS: 'shopIds'
} as const;

export type PersistKey = (typeof PersistKeys)[keyof typeof PersistKeys];

export interface PersistValueMap {
    [PersistKeys.SHOP_IDS]: string[];
}

const STORAGE_KEY = 'wanzl-connect-persisted';

const isPersistKey = (key: string): key is PersistKey => (Object.values(PersistKeys) as string[]).includes(key);

const readStoredPersisted = (): Record<string, unknown> => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (isPersistKey(key) && Array.isArray(value) && value.every((v) => typeof v === 'string')) {
                result[key] = value;
            }
        }
        return result;
    } catch {
        return {};
    }
};

const writeStoredPersisted = (persisted: Record<string, unknown>): void => {
    try {
        if (Object.keys(persisted).length === 0) {
            sessionStorage.removeItem(STORAGE_KEY);
        } else {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
        }
    } catch (error) {
        console.error('[PersistenceSlice] Failed to store persisted state:', error);
    }
};

export interface PersistenceSlice {
    persisted: Record<string, unknown>;
    getPersisted: <K extends PersistKey>(key: K) => PersistValueMap[K] | undefined;
    setPersisted: <K extends PersistKey>(key: K, value: PersistValueMap[K]) => void;
    clearPersisted: (key?: PersistKey) => void;
    // Call in store teardown (tests, HMR) to remove the ORGANISATION_CHANGED listener.
    _destroyPersistence: () => void;
}

export const createPersistenceSlice: StateCreator<PersistenceSlice> = (set, get) => {
    const unsubscribeOrgChanged = eventBus.on(EventTypes.ORGANISATION_CHANGED, () => {
        set({ persisted: {} });
        writeStoredPersisted({});
    });

    return {
        persisted: readStoredPersisted(),

        getPersisted: <K extends PersistKey>(key: K) => get().persisted[key] as PersistValueMap[K] | undefined,

        setPersisted: <K extends PersistKey>(key: K, value: PersistValueMap[K]) => {
            set((state) => ({ persisted: { ...state.persisted, [key]: value } }));
            writeStoredPersisted(get().persisted);
        },

        clearPersisted: (key?: PersistKey) => {
            set((state) => {
                if (!key) return { persisted: {} };
                const next = { ...state.persisted };
                delete next[key];
                return { persisted: next };
            });
            writeStoredPersisted(get().persisted);
        },

        _destroyPersistence: () => {
            unsubscribeOrgChanged();
        }
    };
};
